import {
  CardContainer,
  CardTitle,
  CardValue,
} from '../../StyledComponents/Card';
import CondorSpinner from '../CondorSpinner';
import { DeviceDeploymentStatesTable } from './DeviceDeploymentStatesTable';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import styled from 'styled-components';
import { IotJobDetails } from './IoTJobDetails';
import { Borderline } from '../../StyledComponents/Borderline';
import { ButtonContainer } from '../../StyledComponents/Buttons';
import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import { useMutation } from '@tanstack/react-query';
import { sendStartInstallToDevice } from '../../services/DevicesService';

const StyledAccordion = styled(Accordion)`
  border: 1px #eee;
  border-radius: 8px;
  font-style: inherit;

  :hover {
    box-shadow: 1px 1px 16px rgba(0, 0, 0, 0.25);
  }
`;

interface DeviceDeploymentState {
  status: string;
  updatedAt: string;
}

interface DeviceDeploymentSlim {
  version: string;
  jobId: string;
  status: string;
  states?: {
    download: DeviceDeploymentState[];
    install?: DeviceDeploymentState[];
  };
}

interface DeviceDeploymentDetailsProps {
  loading: boolean;
  deployments?: DeviceDeploymentSlim[];
  canopyDeviceId?: string;
  refetch: () => void;
}

export const DeviceDeploymentDetails = (
  props: DeviceDeploymentDetailsProps,
) => {
  const { loading, deployments, canopyDeviceId, refetch } = props;
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const { mutate, isPending: startInstallLoading } = useMutation({
    mutationFn: sendStartInstallToDevice,
    onError: (err) => {
      console.error(err);
    },
    onSettled: () => {
      refetch();
    },
  });
  const sendStartInstall = async (jobId: string) => {
    if (!canopyDeviceId || !jobId) {
      console.warn('Missing required input variables');
      return;
    }

    mutate({ canopyDeviceId, jobId });
  };
  return (
    <CardContainer>
      <CardTitle>Deployments For Device</CardTitle>
      {loading ? (
        <CondorSpinner />
      ) : (
        <>
          {deployments?.map((deployment, indexOut) => (
            <StyledAccordion
              slotProps={{ transition: { unmountOnExit: true } }}
              expanded={expanded === `deployments/${indexOut}`}
              onChange={handleChange(`deployments/${indexOut}`)}
              key={indexOut}
            >
              <AccordionSummary
                expandIcon={<GridExpandMoreIcon />}
                aria-controls={`deployments-${indexOut}-content`}
                id={`deployments/${indexOut}`}
              >
                <Typography style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                  {deployment.version} - {deployment.status}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {canopyDeviceId && (
                  <IotJobDetails
                    canopyDeviceId={canopyDeviceId}
                    jobId={deployment.jobId}
                  ></IotJobDetails>
                )}
                <Borderline />
                <CardTitle>Download States</CardTitle>
                {deployment.states && (
                  <DeviceDeploymentStatesTable
                    states={deployment.states.download}
                    deploymentVersion={deployment.version}
                    stateType="download"
                  />
                )}
                <Borderline />
                <CardTitle>Install States</CardTitle>
                {deployment.states && !deployment.states.install ? (
                  <CardValue>No Install State Data</CardValue>
                ) : (
                  deployment.states?.install && (
                    <DeviceDeploymentStatesTable
                      states={deployment.states.install}
                      deploymentVersion={deployment.version}
                      stateType="install"
                    />
                  )
                )}
                {deployment.states?.install?.at(-1)?.status ===
                  'DEVICE_READY' && (
                  <ButtonContainer>
                    <StyledButton
                      disabled={startInstallLoading}
                      onPress={() => sendStartInstall(deployment.jobId)}
                      buttonRules={ButtonRules.primaryA}
                    >
                      Force Install
                    </StyledButton>
                  </ButtonContainer>
                )}
              </AccordionDetails>
            </StyledAccordion>
          ))}
        </>
      )}
    </CardContainer>
  );
};
